import {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import ImageSourceUpload from '../components/imageConverter/ImageSourceUpload'
import ImageFormatSelector from '../components/imageConverter/ImageFormatSelector'
import ImagePreviewCanvas from '../components/imageConverter/ImagePreviewCanvas'
import ImageDownloadPanel from '../components/imageConverter/ImageDownloadPanel'
import Seo from '../components/seo/Seo'
import {SITE_URL} from '../components/seo/seoConstants'
import {CtaSection} from '../components/seo/seoSections'
import {trackEvent} from '../../common/analytics/trackEvent'
import {reportError} from '../../common/errors/reportError'
import {convertImage} from '../../common/utils/imageConverter'
import {DEFAULT_ICO_SIZE, DEFAULT_JPEG_QUALITY, type ImageFormat} from '../../common/constants/converterFormats'
import '../components/seo/SeoPage.css'
import '../components/converterShared/ConverterShared.css'
import './ImageConverterPage.css'

const JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Ecranify Image Converter',
  applicationCategory: 'DesignApplication',
  operatingSystem: 'Web browser',
  url: `${SITE_URL}/image-converter`,
  description: 'Free browser-based image format converter — PNG, JPEG, WebP, GIF, BMP and ICO, all converted locally.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
}

const ImageConverterPage = () => {
  const {t} = useTranslation()

  const [sourceImage, setSourceImage] = useState<HTMLImageElement | null>(null)
  const [isAnimatedGif, setIsAnimatedGif] = useState(false)
  const [format, setFormat] = useState<ImageFormat>('png')
  const [quality, setQuality] = useState(DEFAULT_JPEG_QUALITY)
  const [icoSizes, setIcoSizes] = useState<Set<number>>(new Set([DEFAULT_ICO_SIZE]))
  const [estimatedSizeBytes, setEstimatedSizeBytes] = useState<number | null>(null)

  useEffect(() => {
    trackEvent('image_converter_viewed')
  }, [])

  // Debounced real-encode size estimate — runs the actual converter (same code
  // path as the download) so the number is exact for the current settings, not
  // a guess; debounced so dragging the quality slider doesn't re-encode on every tick.
  useEffect(() => {
    if (!sourceImage) return
    let cancelled = false
    const timer = window.setTimeout(() => {
      convertImage(sourceImage, format, {quality, icoSizes: [...icoSizes]})
        .then((blob) => {
          if (!cancelled) setEstimatedSizeBytes(blob.size)
        })
        .catch((err: unknown) => {
          if (!cancelled) reportError(err, 'image-converter-size-estimate')
        })
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [sourceImage, format, quality, icoSizes])

  const handleImageLoaded = useCallback((image: HTMLImageElement, animatedGif: boolean) => {
    setSourceImage(image)
    setIsAnimatedGif(animatedGif)
  }, [])

  const handleClear = useCallback(() => {
    setSourceImage(null)
    setIsAnimatedGif(false)
    setEstimatedSizeBytes(null)
  }, [])

  const handleFormatChange = useCallback((next: ImageFormat) => {
    setFormat(next)
    trackEvent('image_converter_format_selected', {format: next})
  }, [])

  const handleIcoSizeToggle = useCallback((size: number, enabled: boolean) => {
    setIcoSizes((prev) => {
      const next = new Set(prev)
      if (enabled) next.add(size)
      else next.delete(size)
      return next.size > 0 ? next : new Set([DEFAULT_ICO_SIZE])
    })
  }, [])

  return (
    <div className="imc-root seo-root">
      <Seo
        title="Free Image Format Converter — PNG, JPEG, WebP, GIF, BMP, ICO | Ecranify"
        description="Convert images between PNG, JPEG, WebP, GIF, BMP and ICO for free, right in your browser. No upload, no signup — pick a file, choose a format, download."
        canonicalPath="/image-converter"
        jsonLd={[JSON_LD]}
      />
      <div className="imc-grid" aria-hidden />

      <main className="imc-page">
        <header className="imc-hero">
          <span className="imc-eyebrow">
            <span className="imc-eyebrow-dot" />
            {t('imageConverter.hero.eyebrow')}
          </span>
          <h1 className="imc-title">
            {t('imageConverter.hero.titlePrefix')} <em>{t('imageConverter.hero.titleEm')}</em>
          </h1>
          <p className="imc-lede">{t('imageConverter.hero.subtitle')}</p>
        </header>

        <div className="imc-layout">
          <section className="imc-col imc-col-source">
            <h2 className="imc-section-title">{t('imageConverter.sections.source')}</h2>
            <ImageSourceUpload image={sourceImage} onImageLoaded={handleImageLoaded} onClear={handleClear} />
            {isAnimatedGif && <p className="cvt-hint">{t('imageConverter.upload.gifHint')}</p>}
          </section>

          <section className="imc-col imc-col-preview">
            <h2 className="imc-section-title">{t('imageConverter.sections.preview')}</h2>
            <ImagePreviewCanvas
              image={sourceImage}
              format={format}
              quality={quality}
              onImageLoaded={handleImageLoaded}
              onCropApplied={setSourceImage}
            />
          </section>

          <section className="imc-col imc-col-export">
            <h2 className="imc-section-title">{t('imageConverter.sections.format')}</h2>
            <ImageFormatSelector
              format={format}
              quality={quality}
              icoSizes={icoSizes}
              estimatedSizeBytes={estimatedSizeBytes}
              onFormatChange={handleFormatChange}
              onQualityChange={setQuality}
              onIcoSizeToggle={handleIcoSizeToggle}
            />
            <ImageDownloadPanel image={sourceImage} format={format} quality={quality} icoSizes={[...icoSizes]} />
          </section>
        </div>

        <section className="imc-seo">
          <h2 className="imc-seo-title">{t('imageConverter.seo.title')}</h2>
          <p className="imc-seo-body">{t('imageConverter.seo.body1')}</p>
          <p className="imc-seo-body">{t('imageConverter.seo.body2')}</p>
        </section>

        <CtaSection cta={{title: t('imageConverter.cta.title'), body: t('imageConverter.cta.body')}} />
      </main>
    </div>
  )
}

export default ImageConverterPage
